/**
 * Actividad diaria de TBit Jobs: de qué aviso o anuncio llegó la persona y qué
 * página abrió (2026-10-23).
 *
 * Los avisos llevan `?n=<etiqueta>` y los anuncios `utm_*`. Como el `?ref`
 * (ver `referralCapture.ts`) y el clic del anuncio (`adAttribution.ts`), eso se
 * perdería en el rodeo por auth.tbit.app: se guarda en `sessionStorage` ANTES de
 * montar React y viaja con la primera visita que el servidor acepte. Solo
 * entonces se borra: un 401 (sin sesión todavía) deja la fuente para el
 * siguiente cambio de ruta. Una visita sin parámetros no borra lo guardado.
 *
 * Silenciosa: nadie espera esta llamada, y en «Ver como» no sale nada (el
 * operador no es el candidato). El servidor normaliza la página; se manda el
 * pathname crudo.
 */
import { apiFetch } from '~/lib/api';
import { currentViewAs } from '~/lib/viewAs';

export const ACTIVITY_SOURCE_KEY = 'job_apply_activity_source';

const PARAMS = ['n', 'utm_source', 'utm_campaign'] as const;

type ActivitySource = Partial<Record<(typeof PARAMS)[number], string>>;

/** Lee la URL de entrada y guarda la fuente si trae alguna. */
export function captureActivitySourceFromUrl(search: string = window.location.search): void {
  try {
    const qs = new URLSearchParams(search);
    const source: ActivitySource = {};
    for (const p of PARAMS) {
      const v = qs.get(p)?.trim();
      if (v) source[p] = v.slice(0, 100);
    }
    if (!Object.keys(source).length) return;
    window.sessionStorage.setItem(ACTIVITY_SOURCE_KEY, JSON.stringify(source));
  } catch {
    // Modo privado: se pierde la fuente, no la visita.
  }
}

function readSource(): ActivitySource | null {
  try {
    const raw = window.sessionStorage.getItem(ACTIVITY_SOURCE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActivitySource | null;
    return parsed && Object.keys(parsed).length ? parsed : null;
  } catch {
    return null;
  }
}

function clearSource(): void {
  try {
    window.sessionStorage.removeItem(ACTIVITY_SOURCE_KEY);
  } catch {
    /* nada */
  }
}

/** Una visita por cambio de ruta. Nunca lanza. */
export async function trackPageView(pathname: string): Promise<void> {
  try {
    if (currentViewAs()) return;
    const source = readSource();
    const res = await apiFetch('/job-apply/api/activity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: pathname, ...(source ? { source } : {}) }),
    });
    if (res.ok && source) clearSource();
  } catch {
    // Red caída o sesión vencida: la próxima visita lo intenta de nuevo.
  }
}
